/**
 * Client-side brand canonicalization (mirrors functions/brandResolver.js).
 * `src/data/brandDictionary.json` is a copy of `functions/brandDictionary.json` for CRA imports.
 */
import brandDictionary from '../data/brandDictionary.json';

export function normalizeBrandString(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

let _aliasPairsSorted = null;

function getAliasPairsSortedByLengthDesc() {
  if (_aliasPairsSorted) return _aliasPairsSorted;
  const pairs = [];
  for (const [canonicalKey, meta] of Object.entries(brandDictionary)) {
    const displayName = meta.displayName;
    if (displayName) {
      pairs.push({
        alias: normalizeBrandString(displayName),
        canonicalKey
      });
    }
    for (const a of meta.aliases || []) {
      pairs.push({
        alias: normalizeBrandString(a),
        canonicalKey
      });
    }
  }
  pairs.sort((a, b) => b.alias.length - a.alias.length);
  _aliasPairsSorted = pairs;
  return pairs;
}

export function resolveMakeToCanonicalKey(make) {
  const normalized = normalizeBrandString(make);
  if (!normalized) return null;
  for (const { alias, canonicalKey } of getAliasPairsSortedByLengthDesc()) {
    if (normalized === alias) {
      return canonicalKey;
    }
  }
  return null;
}

/** Common casings for Firestore `make` equality (exact match is case-sensitive). */
function expandMakeQueryValues(strings) {
  const out = new Set();
  for (const s of strings) {
    if (!s || typeof s !== 'string') continue;
    out.add(s);
    out.add(s.toLowerCase());
    out.add(s.toUpperCase());
    const title = s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    out.add(title);
  }
  return [...out];
}

/**
 * Distinct `make` field values to query (expanded for casing). Firestore `in` max 30.
 */
export function getMakeQueryBatchesForCanonicalKey(canonicalKey, maxPerBatch = 30) {
  const meta = brandDictionary[canonicalKey];
  if (!meta) return [];
  const raw = [meta.displayName, ...(meta.aliases || [])].filter(Boolean);
  const expanded = expandMakeQueryValues(raw);
  if (!expanded.length) return [];
  const batches = [];
  for (let i = 0; i < expanded.length; i += maxPerBatch) {
    batches.push(expanded.slice(i, i + maxPerBatch));
  }
  return batches;
}
